// src/pages/Headquarter/ChangeHeadquarter.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import headquarterService from '../../services/headquarter.service';
import subHeadquarterService from '../../services/subHeadquarter.service';
import regionService from '../../services/region.service';
import companyService from '../../services/company.service';
import '../../styles/AddPages.css';

const ChangeHeadquarter = () => {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();

  // Change HQ Section
  const [changeHQForm, setChangeHQForm] = useState({
    companyId: '',
    currentHQId: '',
    newRegionId: '',
  });

  // Change Sub-HQ Section
  const [changeSubHQForm, setChangeSubHQForm] = useState({
    companyId: '',
    currentSubHQId: '',
    newHQId: '',
    newRegionId: '',
  });

  const [companies, setCompanies] = useState([]);
  const [hqRegions, setHQRegions] = useState([]);
  const [headquarters, setHeadquarters] = useState([]);
  const [subHQRegions, setSubHQRegions] = useState([]);
  const [subHeadquarters, setSubHeadquarters] = useState([]);
  const [newHQs, setNewHQs] = useState([]);
  
  const [hqErrors, setHQErrors] = useState({});
  const [subHQErrors, setSubHQErrors] = useState({});
  const [isSubmittingHQ, setIsSubmittingHQ] = useState(false);
  const [isSubmittingSubHQ, setIsSubmittingSubHQ] = useState(false);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchCompanies();
    } else {
      const userCompanyId = user?.companyId || '';
      setChangeHQForm(prev => ({ ...prev, companyId: userCompanyId }));
      setChangeSubHQForm(prev => ({ ...prev, companyId: userCompanyId }));
      
      if (userCompanyId) {
        fetchHQRegionsAndHeadquarters(userCompanyId);
        fetchSubHQRegionsAndSubHeadquarters(userCompanyId);
      }
    }
  }, [isSuperAdmin, user]);

  const fetchCompanies = async () => {
    try {
      const response = await companyService.getAllCompanies();
      if (response.success && response.data) {
        setCompanies(response.data);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchHQRegionsAndHeadquarters = async (companyId) => {
    try {
      // Fetch regions
      const regionsResp = await regionService.getAllRegions(companyId);
      if (regionsResp.success && regionsResp.data) {
        setHQRegions(regionsResp.data);
      }

      // Fetch all HQs for dropdown
      const hqResp = await headquarterService.getHeadquarters(companyId, 1, 1000);
      if (hqResp.success && hqResp.data?.headquarters) {
        setHeadquarters(hqResp.data.headquarters);
      }
    } catch (error) {
      console.error('Error fetching HQ data:', error);
    }
  };

  const fetchSubHQRegionsAndSubHeadquarters = async (companyId) => {
    try {
      // Fetch regions
      const regionsResp = await regionService.getAllRegions(companyId);
      if (regionsResp.success && regionsResp.data) {
        setSubHQRegions(regionsResp.data);
      }

      // Fetch all Sub-HQs for dropdown
      const subHQResp = await subHeadquarterService.getSubHeadquarters(companyId, 1, 1000);
      if (subHQResp.success && subHQResp.data?.subHeadquarters) {
        setSubHeadquarters(subHQResp.data.subHeadquarters);
      }
    } catch (error) {
      console.error('Error fetching Sub-HQ data:', error);
    }
  };

  const fetchNewHQsByRegion = async (regionId) => {
    try {
      const response = await headquarterService.getHeadquartersByRegion(regionId);
      if (response.success && response.data) {
        setNewHQs(response.data);
      }
    } catch (error) {
      console.error('Error fetching new HQs:', error);
      setNewHQs([]);
    }
  };

  // Handle Change HQ
  const handleChangeHQ = (e) => {
    const { name, value } = e.target;
    setChangeHQForm(prev => ({ ...prev, [name]: value }));

    if (name === 'companyId' && value) {
      fetchHQRegionsAndHeadquarters(value);
      setChangeHQForm(prev => ({ ...prev, currentHQId: '', newRegionId: '' }));
    }

    if (hqErrors[name]) {
      setHQErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmitChangeHQ = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!changeHQForm.currentHQId) newErrors.currentHQId = 'Please select a headquarter';
    if (!changeHQForm.newRegionId) newErrors.newRegionId = 'Please select new region';

    if (Object.keys(newErrors).length > 0) {
      setHQErrors(newErrors);
      return;
    }

    setIsSubmittingHQ(true);

    try {
      const response = await headquarterService.updateHeadquarter(
        changeHQForm.currentHQId,
        { regionId: parseInt(changeHQForm.newRegionId) }
      );

      if (response.success) {
        alert('Headquarter region changed successfully!');
        setChangeHQForm({ companyId: changeHQForm.companyId, currentHQId: '', newRegionId: '' });
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to change HQ region');
    } finally {
      setIsSubmittingHQ(false);
    }
  };

  // Handle Change Sub-HQ
  const handleChangeSubHQ = (e) => {
    const { name, value } = e.target;
    setChangeSubHQForm(prev => ({ ...prev, [name]: value }));

    if (name === 'companyId' && value) {
      fetchSubHQRegionsAndSubHeadquarters(value);
      setChangeSubHQForm(prev => ({ ...prev, currentSubHQId: '', newRegionId: '', newHQId: '' }));
    }

    if (name === 'newRegionId' && value) {
      fetchNewHQsByRegion(value);
      setChangeSubHQForm(prev => ({ ...prev, newHQId: '' }));
    }

    if (subHQErrors[name]) {
      setSubHQErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmitChangeSubHQ = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!changeSubHQForm.currentSubHQId) newErrors.currentSubHQId = 'Please select a sub-headquarter';
    if (!changeSubHQForm.newRegionId) newErrors.newRegionId = 'Please select new region';
    if (!changeSubHQForm.newHQId) newErrors.newHQId = 'Please select new headquarter';

    if (Object.keys(newErrors).length > 0) {
      setSubHQErrors(newErrors);
      return;
    }

    setIsSubmittingSubHQ(true);

    try {
      const response = await subHeadquarterService.changeSubHeadquarterHQ({
        subHeadquarterId: parseInt(changeSubHQForm.currentSubHQId),
        newHeadquarterId: parseInt(changeSubHQForm.newHQId),
        newRegionId: parseInt(changeSubHQForm.newRegionId),
      });

      if (response.success) {
        alert('Sub-Headquarter changed successfully!');
        setChangeSubHQForm({ 
          companyId: changeSubHQForm.companyId, 
          currentSubHQId: '', 
          newRegionId: '',
          newHQId: ''
        });
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to change Sub-HQ');
    } finally {
      setIsSubmittingSubHQ(false);
    }
  };

  return (
    <div className="change-headquarter-page">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a href="/dashboard/overview">Dashboard</a>
          </li>
          <li className="breadcrumb-item active">Change HQ/Sub HQ</li>
        </ol>
      </nav>

      {/* Page Header */}
      <div className="page-header">
        <h4 className="page-title">Change Headquarter / Sub-Headquarter</h4>
      </div>

      {/* Change HQ Section */}
      <div className="form-card" style={{ marginBottom: '24px' }}>
        <form onSubmit={handleSubmitChangeHQ}>
          <div className="form-section">
            
            <div className="form-row">
              {isSuperAdmin && (
                <div className="form-group">
                  <label className="form-label">Select Company:</label>
                  <select
                    name="companyId"
                    className="form-input"
                    value={changeHQForm.companyId}
                    onChange={handleChangeHQ}
                  >
                    <option value="">Select Company</option>
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Select Headquarter:</label>
                <select
                  name="currentHQId"
                  className={`form-input ${hqErrors.currentHQId ? 'error' : ''}`}
                  value={changeHQForm.currentHQId}
                  onChange={handleChangeHQ}
                  disabled={isSuperAdmin && !changeHQForm.companyId}
                >
                  <option value="">Select Headquarter</option>
                  {headquarters.map(hq => (
                    <option key={hq.id} value={hq.id}>{hq.name} ({hq.region?.name})</option>
                  ))}
                </select>
                {hqErrors.currentHQId && (
                  <span className="error-message">{hqErrors.currentHQId}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Select New Region:</label>
                <select
                  name="newRegionId"
                  className={`form-input ${hqErrors.newRegionId ? 'error' : ''}`}
                  value={changeHQForm.newRegionId}
                  onChange={handleChangeHQ}
                >
                  <option value="">Select New Region</option>
                  {hqRegions.map(region => (
                    <option key={region.id} value={region.id}>{region.name}</option>
                  ))}
                </select>
                {hqErrors.newRegionId && (
                  <span className="error-message">{hqErrors.newRegionId}</span>
                )}
              </div>
            </div>

            <div className="form-actions" style={{ borderTop: 'none', paddingTop: '0', marginTop: '0' }}>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmittingHQ}
                style={{ backgroundColor: '#f97316' }}
              >
                {isSubmittingHQ ? 'Changing...' : 'Change HQ'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Change Sub-HQ Section */}
      <div className="form-card">
        <form onSubmit={handleSubmitChangeSubHQ}>
          <div className="form-section">
    
            <div className="form-row">
              {isSuperAdmin && (
                <div className="form-group">
                  <label className="form-label">Select Company:</label>
                  <select
                    name="companyId"
                    className="form-input"
                    value={changeSubHQForm.companyId}
                    onChange={handleChangeSubHQ}
                  >
                    <option value="">Select Company</option>
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Select Sub Headquarter:</label>
                <select
                  name="currentSubHQId"
                  className={`form-input ${subHQErrors.currentSubHQId ? 'error' : ''}`}
                  value={changeSubHQForm.currentSubHQId}
                  onChange={handleChangeSubHQ}
                  disabled={isSuperAdmin && !changeSubHQForm.companyId}
                >
                  <option value="">Select Sub HQ</option>
                  {subHeadquarters.map(subHQ => (
                    <option key={subHQ.id} value={subHQ.id}>
                      {subHQ.name} ({subHQ.headquarter?.name})
                    </option>
                  ))}
                </select>
                {subHQErrors.currentSubHQId && (
                  <span className="error-message">{subHQErrors.currentSubHQId}</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Select New Region:</label>
                <select
                  name="newRegionId"
                  className={`form-input ${subHQErrors.newRegionId ? 'error' : ''}`}
                  value={changeSubHQForm.newRegionId}
                  onChange={handleChangeSubHQ}
                >
                  <option value="">Select New Region</option>
                  {subHQRegions.map(region => (
                    <option key={region.id} value={region.id}>{region.name}</option>
                  ))}
                </select>
                {subHQErrors.newRegionId && (
                  <span className="error-message">{subHQErrors.newRegionId}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Select New HQ:</label>
                <select
                  name="newHQId"
                  className={`form-input ${subHQErrors.newHQId ? 'error' : ''}`}
                  value={changeSubHQForm.newHQId}
                  onChange={handleChangeSubHQ}
                  disabled={!changeSubHQForm.newRegionId}
                >
                  <option value="">Select New HQ</option>
                  {newHQs.map(hq => (
                    <option key={hq.id} value={hq.id}>{hq.name}</option>
                  ))}
                </select>
                {subHQErrors.newHQId && (
                  <span className="error-message">{subHQErrors.newHQId}</span>
                )}
              </div>
            </div>

            <div className="form-actions" style={{ borderTop: 'none', paddingTop: '0', marginTop: '0' }}>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmittingSubHQ}
                style={{ backgroundColor: '#f97316' }}
              >
                {isSubmittingSubHQ ? 'Changing...' : 'Change Sub HQ'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangeHeadquarter;